import React from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import type {Assumption} from "../briefingTypes";
import {assumptionDefault, formatAssumptionValue} from "../briefingLogic";
import {useBriefing} from "../briefingState";

type Props = {
    assumption: Assumption
    open: boolean
    onClose: () => void
};

/**
 * The single editor for the five editable assumptions. An override cannot be
 * saved without a reason — the reason is the audit trail and stays visible on
 * the card afterwards. The fixture's propagates_to names the blast radius.
 */
export const AssumptionEditor: React.FC<Props> = ({assumption, open, onClose}) => {
    const {state, dispatch} = useBriefing();
    const override = state.assumptionOverrides[assumption.id];
    const current = override?.value ?? assumption.value;

    const [value, setValue] = React.useState(String(current));
    const [reason, setReason] = React.useState('');
    React.useEffect(() => {
        if (open) {
            setValue(String(override?.value ?? assumption.value));
            setReason('');
        }
    }, [open, override, assumption]);

    const step = assumption.unit === 'factor' ? 0.01 : assumption.unit === '%' ? 0.1 : 1;
    const parsed = parseFloat(value);
    const canApply = Number.isFinite(parsed) && parsed > 0 && reason.trim().length > 0;

    const apply = () => {
        if (!canApply) return;
        dispatch({type: 'setAssumption', id: assumption.id, value: parsed, reason: reason.trim()});
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>{assumption.label}</DialogTitle>
            <DialogContent>
                <div className="db-assumption-meta">
                    <span>Source: {assumption.source}</span>
                    <span>Propagates to: {assumption.propagates_to}</span>
                    {override &&
                        <span className="db-assumption-overridden">
                            Overridden from {formatAssumptionValue(assumption.id, assumptionDefault(assumption.id))} — “{override.reason}”
                        </span>
                    }
                </div>
                <div className="db-assumption-fields">
                    <TextField
                        size="small"
                        type="number"
                        label={assumption.unit === '%' ? '%' : assumption.unit}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        slotProps={{htmlInput: {step}}}
                        sx={{width: 120}}
                        autoFocus
                    />
                    <TextField
                        size="small"
                        fullWidth
                        label="Reason (required)"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                {override &&
                    <Button
                        size="small"
                        sx={{marginRight: 'auto'}}
                        onClick={() => {
                            dispatch({type: 'resetAssumption', id: assumption.id});
                            onClose();
                        }}
                    >
                        Reset to {formatAssumptionValue(assumption.id, assumptionDefault(assumption.id))}
                    </Button>
                }
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={!canApply} onClick={apply}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};
